import { Location } from '@angular/common';
import { Component, Inject, isDevMode, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterEvent } from '@angular/router';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, AuthenticationResult, RedirectRequest } from '@azure/msal-browser';
import {
  AuthService,
  isDefinedNotNull,
  isDefinedNotNullNotEmpty,
  isUndefinedOrNull,
  isUndefinedOrNullOrEmpty,
  UserPermission
} from '@psnb/services';
import { filter, ObservableInput, of, Subject, takeUntil } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CommonService } from '../common/common.service';
import { CreateAssetDialogComponent } from './create-asset-dialog/create-asset-dialog.component';
import { MessageService } from './message.service';
import { GeneratorEditForm } from './power-supply.model';

@Component({
  selector: 'nwt-power-supply',
  styleUrls: ['./power-supply.component.scss'],
  templateUrl: './power-supply.component.html'
})
export class PowerSupplyComponent implements OnDestroy, OnInit {
  public generatorEditDisableButtonObj: GeneratorEditForm = { generatorEditForm: true };
  public createAEdit: boolean = false;
  public createDisableButtonObj: { createlocform: boolean } =  { createlocform: true };
  public disableButtonObj: { locform: boolean } =  { locform: true };
  public isAssetCreatePage: boolean = false;
  public isAssetEditPage: boolean = false;
  public isAssetSearchResultsPage: boolean = false;
  public isCreatePage: boolean = false;
  public isCreateSavePage: boolean = false;
  public isDarkTheme: boolean = false;
  public isDetailPage: boolean = false;
  public isEditPage: boolean = false;
  public isHomePage: boolean = false;
  public isListPage: boolean = false;
  public message: string;
  public psnbUser: boolean = false;
  public showCreatePS: number = 1;
  public telemetryAlertPage: boolean = false;
  public unauthorizedPath: boolean = false;

  private destroying$: Subject<void>;
  private paramsNodeId: string;
  private psCreateAssetDialogComponent: MatDialogRef<CreateAssetDialogComponent> | undefined;
  private userPermissions: string;

  public constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: AuthService,
    private commonService: CommonService,
    private data: MessageService,
    private dialog: MatDialog,
    private location: Location,
    private msalService: MsalService,
    private route: ActivatedRoute,
    private router: Router,
    private title: Title
  ) {
    this.destroying$ = new Subject<void>();
  }

  public async ngOnInit(): Promise<void> {
    this.title.setTitle('Power Supply Notebook');
    this.message = 'Please wait, loading...';
    this.isDarkTheme = localStorage.getItem('theme') === 'Dark';

    this.login();

    this.subscribeToSaves();

    this.route.queryParams
      .pipe(takeUntil(this.destroying$))
      .subscribe((params: Params): void => {
        this.paramsNodeId = params.nodeId;
      });

    this.subscribeToRouterEvents();
  }

  public ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  private login(): void {
    const activeAccount: AccountInfo = this.msalService.instance.getActiveAccount();
    if (isDefinedNotNull(activeAccount)) {
      this.checkUserDetails(); // Already logged in
    } else {
      if (isDefinedNotNull(this.msalGuardConfig.authRequest)) {
        this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
        this.msalService.loginRedirect();
      }
    }

    this.msalService.handleRedirectObservable()
      .pipe(
        catchError((error: unknown): ObservableInput<undefined> => {
          if (isDevMode()) {
            // eslint-disable-next-line no-console
            console.error('MSAL redirect error', error);
          } else {
            this.commonService.loggerPost('MSAL login redirect error', 'login', 5);
          }
          return of(undefined);
        })
      )
      .subscribe((response: AuthenticationResult | undefined): void => {
        if (isDefinedNotNull(response)) {
          this.checkUserDetails();
        }
      });
  }

  private async checkUserDetails(retry: boolean = true): Promise<void> {
    if (await this.authService.checkUserDetails()) {
      this.checkAuthorized();
    } else if (retry) {
      this.authService.getAccessToken(environment.scope)
        .pipe(take(1))
        .subscribe((token: string): void => {
          if (isDefinedNotNullNotEmpty(token)) {
            if (isUndefinedOrNull(this.authService.userDetails)) {
              this.checkUserDetails(false);
            }
          } else if (isUndefinedOrNullOrEmpty(token)) {
            this.message = 'Service is down! Please try again later';
          }
        });
    }
  }

  private checkAuthorized(): void {
    this.userPermissions = localStorage.getItem('user-permissions');
    this.message = undefined;

    if (isDefinedNotNullNotEmpty(this.userPermissions) && this.authService.isAuthorizedPSNBUser) {
      this.psnbUser = true;

      const userPerms: UserPermission[] = JSON.parse(this.userPermissions);
      for (const permission of userPerms) {
        if (permission.category === CommonService.SEARCHBYNODE && permission.functionality === CommonService.CREATEPS) {
          this.showCreatePS = this.permissionLevel(permission.permission);
        }
      }
    } else {
      this.unauthorizedPath = true;
      this.router.navigate(['/unauthorized']);
    }
  }

  private subscribeToSaves(): void {
    this.data.editsaveEnabled
      .pipe(takeUntil(this.destroying$))
      .subscribe((res: { locform: boolean }): void => {
        this.disableButtonObj = res;
      });

    this.data.createSaveEnable$
      .pipe(takeUntil(this.destroying$))
      .subscribe((saveRes: { createlocform: boolean }): void => {
        this.createDisableButtonObj  = saveRes;
      });

    this.data.generatorEditSaveEnabled$
      .pipe(takeUntil(this.destroying$))
      .subscribe((generatorEditRes: GeneratorEditForm): void => {
        this.generatorEditDisableButtonObj = generatorEditRes;
      });
  }

  private subscribeToRouterEvents(): void {
    // Activate the bottom bar icon - url based load
    this.router.events
      .pipe(
        // @ts-ignore: Casting of Event_2 into Event (especially when it is exported as such)
        filter((event: Event | RouterEvent): event is RouterEvent => event),
        takeUntil(this.destroying$)
      )
      .subscribe((event: RouterEvent): void => {
        this.disableButtonObj =  { locform: true };
        this.createDisableButtonObj =  { createlocform: true };
        this.generatorEditDisableButtonObj =  { generatorEditForm: true };
        if (event instanceof NavigationEnd) {
          if (event.url.includes('/createasset')) {
            this.isAssetCreatePage = true;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = false;
            this.isListPage = false;
            this.isHomePage = false;
            this.isDetailPage = false;
            this.isEditPage = false;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.telemetryAlertPage = false;
            this.createAEdit = false;
          } else if (event.url.includes('/assetedit')) {
            this.isAssetCreatePage = false;
            this.isAssetEditPage = true;
            this.isAssetSearchResultsPage = false;
            this.isListPage = false;
            this.isHomePage = false;
            this.isDetailPage = false;
            this.isEditPage = false;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.telemetryAlertPage = false;
            this.createAEdit = false;
          } else if (event.url.includes('/list')) {
            this.isAssetCreatePage = false;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = false;
            this.isListPage = true;
            this.isHomePage = false;
            this.isDetailPage = false;
            this.isEditPage = false;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.telemetryAlertPage = false;
            this.createAEdit=false;
          } else if (event.url.includes('/telemetry')|| event.url.includes('/alerts')) {
            this.isAssetCreatePage = false;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = false;
            this.isListPage = false;
            this.isHomePage = false;
            this.isDetailPage = false;
            this.isEditPage = false;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.telemetryAlertPage = true;
            this.createAEdit=false;
          } else if (event.url.includes('/pssearch')) {
            this.isAssetCreatePage = false;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = false;
            this.isHomePage = true;
            this.isListPage = false;
            this.isDetailPage = false;
            this.isEditPage = false;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.telemetryAlertPage = false;
            this.createAEdit=false;
          } else if (event.url.includes('/details')) {
            this.isAssetCreatePage = false;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = false;
            this.isHomePage = false;
            this.isListPage = false;
            this.isDetailPage = true;
            this.isEditPage = false;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.telemetryAlertPage = false;
            this.createAEdit=false;
          } else if (event.url.includes('/edit')) {
            this.isAssetCreatePage = false;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = false;
            this.isHomePage = false;
            this.isListPage = false;
            this.isDetailPage = false;
            this.isEditPage = true;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.telemetryAlertPage = false;
            this.createAEdit=false;
          } else if (event.url.includes('/create')) {
            this.isAssetCreatePage = false;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = false;
            this.isHomePage = false;
            this.isListPage = false;
            this.isDetailPage = false;
            this.isEditPage = false;
            this.isCreatePage = false;
            this.isCreateSavePage = true;
            this.telemetryAlertPage = false;
            this.createAEdit=false;
          } else if (
            event.url.includes('/assetsearchdata') ||
            event.url.includes('/assetdetails') ||
            event.url.includes('/nodeCommunintyServed')
          ) {
            this.createAEdit = false;
            this.isAssetCreatePage = false;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = true;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.isDetailPage = false;
            this.isEditPage = false;
            this.isHomePage = false;
            this.isListPage = false;
            this.telemetryAlertPage = false;
          } else {
            this.createAEdit = false;
            this.isAssetCreatePage = false;
            this.isAssetEditPage = false;
            this.isAssetSearchResultsPage = false;
            this.isCreatePage = false;
            this.isCreateSavePage = false;
            this.isDetailPage = false;
            this.isEditPage = false;
            this.isHomePage = true;
            this.isListPage = false;
            this.telemetryAlertPage = false;
          }
        }

        this.route.queryParams
          .pipe(take(1))
          .subscribe((params: Params): void => {
            if (params.nodeCommunintyServed && !this.isAssetSearchResultsPage) {
              this.createAEdit = false;
              this.isAssetSearchResultsPage = false;
              this.isCreatePage = true;
              this.isCreateSavePage = false;
              this.isDetailPage = false;
              this.isEditPage = false;
              this.isHomePage = false;
              this.isListPage = false;
            } else if (
              params.nodeId &&
              !this.isCreateSavePage &&
              !this.isAssetSearchResultsPage
            ) {
              this.isCreatePage = false;
              this.isHomePage = false;
              this.isListPage = false;
              this.isDetailPage = false;
              this.isEditPage = false;
              this.isCreateSavePage = false;
              this.createAEdit=true;
            }
          });
      });
  }

  /**
   * Function to choose the dark and white theme - toggle Switch
   */
  public themeSelection(): void {
    localStorage.setItem('theme', this.isDarkTheme ? 'Dark' : 'Light');
  }

  /**
   * Function to save the button -  Save
   */
  public submitAction(event: Event): void {
    event.preventDefault();
    this.disableButtonObj =  { locform: true };
    this.data.changeMessage('issubmit', true);
  }

  /**
   * Function to create save the button -  Save
   */
  public submitCreateAction(event: Event): void {
    event.preventDefault();
    this.createDisableButtonObj =  { createlocform: true };
    this.data.saveMessageCreate('iscreatesubmit', true);
  }

  /**
   * Function to generator edit save the button -  Save
   */
  public submitGeneratorEditAction(event: Event): void {
    event.preventDefault();
    this.generatorEditDisableButtonObj = { generatorEditForm: true };
    this.data.generatorEditSaveMessage('isGeneratorEditSubmit', true);
  }

  /*Function to go back from edit view to list view - Edit view based */
  public goBack(): void {
    this.location.back();
  }

  public routeToCreate(): void {
    this.router.navigate(['/create'], { queryParams: { nodeId: this.paramsNodeId } });
  }

  public openCreateAssetDialog(): void {
    // Return early if dialog is already open
    if (this.psCreateAssetDialogComponent?.getState() === MatDialogState.OPEN) {
      return;
    }

    this.psCreateAssetDialogComponent = this.dialog.open(CreateAssetDialogComponent, {
      disableClose: true,
      height: '230px',
      panelClass: 'my-centered-dialog',
      width: '512px'
    });
  }

  private permissionLevel(level: string): number {
    if (level === CommonService.DISABLED) {
      return 1;
    } else if (level === CommonService.ENABLED) {
      return 0;
    } else {
      return 2;
    }
  }
}
